import { supportedUnits } from "./units.js";
import { assertSafe } from "./schema.js";
import { catalog } from "./catalog.js";
import {
  enumField as e,
  numberField as n,
  booleanField as b,
  textField as s,
  integerField as i,
  arrayField as a,
  objectField as o,
  stringList as l,
  boundedMap as m,
  assertValid,
  ValidationError,
  deepFreeze,
} from "./schema.js";

const point = o({
  value: n(0),
  label: s(""),
  tone: e(["accent", "good", "attention", "danger", "muted"], "accent"),
});
const zone = o(
  {
    min: n(0),
    max: n(100),
    label: s(""),
    tone: e(["good", "attention", "danger", "accent"], "attention"),
  },
  {},
  ["min", "max"],
);
const pointer = o({
  role: s("heading", 40),
  style: e(["needle", "chevron", "tick"], "needle"),
  badge: b(true),
});
const mapping = m(
  o({
    label: s(""),
    tone: e(["good", "attention", "danger", "muted", "accent"], "muted"),
  }),
  { unknown: { label: "Unknown", tone: "muted" } },
);
const row = o(
  {
    role: s("value", 40),
    label: s(""),
    unit: e(supportedUnits, "inherit"),
    roleUnits: m(e(supportedUnits, "inherit"), {}, 20),
    roleDecimals: m(e(["auto", "0", "1", "2", "3"], "auto"), {}, 20),
    decimals: i(1, 0, 3),
  },
  {},
  ["role"],
);
const bounds = a(n(0), [-50, 50], 2);
const overlay = o(
  {
    role: s("value", 40),
    label: s(""),
    x: n(0.5, 0, 1),
    y: n(0.5, 0, 1),
    controlId: e(
      [
        "number",
        "trend-number",
        "state",
        "text-value",
        "arc-gauge",
        "needle-gauge",
        "vertical-meter",
        "horizontal-meter",
        "borderless-bar",
        "tank",
      ],
      "number",
    ),
    width: n(0.2, 0.05, 1),
  },
  {},
  ["role", "x", "y"],
);
export const commonFields = {
  title: s("", 80),
  subtitle: s("", 120),
  showTitle: b(true),
  showSource: b(true),
  showAge: b(true),
  unit: e(supportedUnits, "inherit"),
  decimals: e(["auto", "0", "1", "2", "3"], "auto"),
  sign: e(["auto", "always"], "auto"),
  leadingZeros: b(false),
  scaleMode: e(["auto", "fixed"], "auto"),
  scaleMin: n(0),
  scaleMax: n(100),
  ticks: i(5, 2, 12),
  size: e(["s", "m", "l"], "m"),
  hidden: b(false),
  order: i(0, 0, 1000),
  density: e(["comfortable", "compact"], "comfortable"),
  theme: e(["light", "dark", "night"], "dark"),
  variant: e(["detailed", "simple"], "detailed"),
  animation: e(["smooth", "off"], "smooth"),
  animationDuration: i(1400, 0, 5000),
  accent: e(
    ["coral", "cyan", "blue", "green", "orange", "purple", "red"],
    "coral",
  ),
  font: e(["sans", "mono"], "sans"),
  surface: e(["solid", "transparent"], "solid"),
  lineWidth: n(2.5, 1, 5),
  pointerStyle: e(["needle", "chevron", "tick"], "chevron"),
  historyWindow: e(["1h", "24h", "7d", "30d"], "24h"),
  rolling: b(true),
  curve: e(["linear", "smooth", "step"], "linear"),
  aggregation: e(["mean", "min", "max", "last", "circular"], "mean"),
  smoothing: e(["off", "mean-3"], "off"),
  legend: e(["auto", "show", "hide"], "auto"),
  extremes: e(["off", "window", "local"], "off"),
  zones: a(zone, [], 6),
  markers: a(point, [], 12),
  detail: e(["none", "history", "system"], "none"),
  fullscreen: b(true),
  timezone: s("UTC", 80),
};
commonFields.order["x-scopes"] = ["panel"];
const options = {
  compass: {
    orientation: e(["north-up", "heading-up"], "north-up"),
    pointer: e(["needle", "chevron", "tick"], "needle"),
    ticks: e(["coarse", "fine"], "coarse"),
    center: e(["heading", "none"], "heading"),
    cardinalLabels: b(true),
  },
  "head-up": {
    orientation: e(["heading-up"], "heading-up"),
    pointers: a(
      pointer,
      [{ role: "heading", style: "needle", badge: true }],
      6,
    ),
    centerRole: e(["heading", "course", "speed"], "heading"),
    sectorArcs: a(zone, [], 4),
  },
  "wind-rose": {
    windDesign: e(
      ["auto", "halo", "sail", "tactical", "clear", "orbit", "petal"],
      "auto",
    ),
    frame: e(["relative", "absolute"], "relative"),
    orientation: e(["bow-up", "north-up"], "bow-up"),
    pointers: a(
      pointer,
      [
        { role: "apparentAngle", style: "needle", badge: true },
        { role: "trueAngle", style: "chevron", badge: true },
      ],
      6,
    ),
    centerRole: e(["apparentSpeed", "trueSpeed"], "trueSpeed"),
    speedLabels: b(true),
    sectors: a(zone, [], 4),
  },
  "close-wind": {
    expandedArc: a(n(0, 0, 180), [20, 60], 2),
    scaleMode: e(["close-hauled"], "close-hauled"),
    tackLabels: b(true),
    targetAngle: n(40, 0, 180),
    historyTrail: e(["off", "short"], "off"),
  },
  "wind-angle": {
    frame: e(["relative"], "relative"),
    bounds: a(n(0, -180, 180), [-180, 180], 2),
    center: e(["angle", "speed"], "angle"),
    portStarboardLabels: b(true),
    targetSectors: a(zone, [], 4),
  },
  rudder: {
    bounds,
    zeroLabel: s("Centered", 40),
    showOrder: b(true),
    orderStyle: e(["tick", "needle"], "tick"),
    portStarboardLabels: b(true),
  },
  "turn-rate": {
    range: bounds,
    deadband: n(0, 0, 100),
    centerMark: b(true),
    pointerStyle: e(["needle", "bar"], "needle"),
  },
  heel: {
    axis: e(["roll", "pitch", "both"], "roll"),
    scale: bounds,
    zeroReference: s("source-calibration", 80),
    display: e(["arc", "bubble"], "arc"),
    visualLimit: n(25, 0, 90),
  },
  attitude: {
    horizon: b(true),
    pitchLadder: b(true),
    rollTicks: a(n(0, -180, 180), [-60, -30, 0, 30, 60], 12),
    centerMarker: e(["fixed"], "fixed"),
    headingStrip: b(false),
  },
  "set-drift": {
    orientation: e(["north-up", "heading-up"], "north-up"),
    vectorScale: e(["auto", "fixed"], "auto"),
    showReference: b(true),
    showConfidence: b(true),
  },
  number: {
    readoutDesign: e(
      ["auto", "odometer", "horizon", "ribbon", "focus", "line", "capsule"],
      "auto",
    ),
    alignment: e(["start", "center", "end"], "start"),
    valueSize: e(["normal", "hero"], "hero"),
    captionPosition: e(["above", "below"], "above"),
    secondaryRole: s("", 40),
  },
  "trend-number": {
    readoutDesign: e(
      ["auto", "odometer", "horizon", "ribbon", "focus", "line", "capsule"],
      "auto",
    ),
    historyPlacement: e(["below", "behind"], "below"),
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    extremes: e(["off", "window"], "window"),
    delta: e(["off", "absolute", "percent"], "absolute"),
    sparkHeight: e(["compact", "normal"], "normal"),
  },
  state: {
    display: e(["dot", "label", "symbol"], "dot"),
    mappings: mapping,
    unknownLabel: s("Unknown", 40),
    pulse: e(["off"], "off"),
  },
  "text-value": {
    wrap: b(false),
    alignment: e(["start", "center"], "start"),
    emptyLabel: s("—", 40),
    maxLines: i(1, 1, 3),
    enumLabels: m(s(""), {}, 32),
  },
  "multi-text": {
    rows: a(row, [], 6),
    layout: e(["stacked", "two-column"], "stacked"),
    labelWidth: e(["auto"], "auto"),
    separators: b(false),
    wrap: b(true),
  },
  "status-strip": {
    cells: a(row, [], 8),
    labelMode: e(["short", "full"], "short"),
    overflow: e(["wrap"], "wrap"),
    includeHealth: b(true),
  },
  counter: {
    countMode: e(["total", "period"], "total"),
    period: e(["day", "week", "month", "session"], "day"),
    digits: i(1, 1, 8),
    showReset: b(false),
    resetScope: s("", 80),
  },
  timer: {
    mode: e(["elapsed", "remaining"], "elapsed"),
    format: e(["auto", "hh:mm:ss"], "auto"),
    period: e(["total", "session"], "total"),
    showRunningState: b(true),
    reset: b(false),
  },
  "analog-clock": {
    zone: s("UTC", 80),
    secondHand: b(false),
    labels: e(["cardinal", "all"], "cardinal"),
    format: e(["12-hour"], "12-hour"),
    sourceLabel: b(true),
  },
  "digital-clock": {
    zone: s("UTC", 80),
    format: e(["12h", "24h"], "24h"),
    seconds: b(false),
    date: b(true),
  },
  "arc-gauge": {
    min: n(0),
    max: n(100),
    sweep: e(["three-quarter", "half", "full"], "three-quarter"),
    referenceMarker: n(0),
    tickLabels: b(true),
    showValue: b(true),
  },
  "needle-gauge": {
    range: a(n(0), [0, 100], 2),
    sweep: e(["full", "three-quarter", "half"], "three-quarter"),
    ticks: i(5, 2, 12),
    valuePosition: e(["center", "below"], "below"),
  },
  vacuum: {
    direction: e(
      ["increasing-vacuum", "absolute-pressure", "gauge-pressure"],
      "increasing-vacuum",
    ),
    range: a(n(0), [0, 100], 2),
    serviceMarker: n(0),
    referenceLabel: b(true),
  },
  "vertical-meter": {
    orientation: e(["vertical"], "vertical"),
    bounds: a(n(0), [0, 100], 2),
    direction: e(["highGood", "lowGood", "neutral"], "neutral"),
    thresholds: a(point, [], 6),
    showPercent: b(true),
  },
  "horizontal-meter": {
    orientation: e(["horizontal"], "horizontal"),
    valuePosition: e(["inline", "above"], "inline"),
    endpoints: b(true),
    targetMarker: n(0),
    fillDirection: e(["forward", "reverse"], "forward"),
  },
  "borderless-bar": {
    orientation: e(["horizontal", "vertical"], "horizontal"),
    track: e(["subtle", "none"], "subtle"),
    labels: e(["value", "percent", "none"], "percent"),
    background: e(["inherit", "transparent"], "inherit"),
    fillDirection: e(["forward", "reverse"], "forward"),
  },
  tank: {
    shape: e(["rectangle", "cylinder", "asset"], "cylinder"),
    asset: s("", 80),
    display: e(["percent", "quantity", "both"], "both"),
    lowMarker: n(0),
    fluidLabel: b(true),
    fillDirection: e(["bottom-up"], "bottom-up"),
  },
  sparkline: {
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    baseline: e(["off", "zero", "reference"], "off"),
    endpointLabel: b(false),
  },
  history: {
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    domain: e(["auto", "zero", "fixed"], "auto"),
    envelope: b(false),
    thresholds: a(point, [], 6),
  },
  "depth-graph": {
    axisDirection: e(["deeper-down"], "deeper-down"),
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    shallowLimit: n(2, 0, 1e5),
    referenceLabel: b(true),
    bottomFill: b(false),
  },
  "state-history": {
    states: mapping,
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    lanes: l([], 6),
    unknownIntervals: e(["visible"], "visible"),
    eventMarkers: b(true),
  },
  "comparison-bars": {
    orientation: e(["vertical", "horizontal"], "vertical"),
    sort: e(["natural", "value"], "natural"),
    baseline: e(["zero"], "zero"),
    partialPeriods: e(["visible"], "visible"),
    labels: e(["auto", "show"], "auto"),
    period: e(["day", "week", "month", "trip"], "day"),
  },
  "multi-graph": {
    sharedTimeAxis: b(true),
    panelOrder: l([], 6),
    domains: m(a(n(0), [0, 100], 2), {}, 6),
    cursor: e(["shared"], "shared"),
    limit: i(4, 1, 6),
  },
  anchor: {
    radius: n(40, 5, 4000),
    trackHours: i(12, 1, 72),
    showVessels: b(true),
    weatherLayers: l([], 4),
    orientation: e(["north-up", "heading-up"], "north-up"),
  },
  gps: {
    fields: l(["fix", "satellites", "accuracy"], 8),
    detail: e(["compact", "full"], "full"),
    receiverLabel: b(true),
    satelliteView: e(["none", "bars", "sky"], "bars"),
  },
  signal: {
    display: e(["bars", "number", "both"], "both"),
    scale: a(n(0), [-110, -40], 2),
    linkLabel: b(true),
    unknownState: e(["visible"], "visible"),
    history: b(false),
  },
  "board-health": {
    include: l([], 40),
    modeScope: e(["all", "current"], "all"),
    severityOrder: e(["platform-defined"], "platform-defined"),
    missingPolicy: e(["visible"], "visible"),
    drilldown: b(true),
  },
  traffic: {
    maxTargets: i(5, 1, 20),
    view: e(["list", "relative-plot"], "relative-plot"),
    photos: b(false),
    radius: n(1852, 100, 185200),
    sort: e(["range", "risk"], "range"),
    labelFields: l(["name", "range"], 6),
  },
  route: {
    orientation: e(["north-up", "heading-up"], "north-up"),
    showTrack: b(true),
    view: e(["map", "leg-list"], "map"),
    fields: l(["distance", "bearing", "ETA"], 6),
    mapLayers: l([], 6),
  },
  forecast: {
    hours: i(12, 3, 48),
    showSeaState: b(true),
    fields: l(["wind", "temperature"], 8),
    layout: e(["hourly", "summary"], "hourly"),
    showIssuedAt: b(true),
  },
  tide: {
    lookbackHours: i(12, 3, 24),
    minRangeMeters: n(0.2, 0, 3),
    showExtrema: b(true),
    datumLabel: b(true),
    stationLabel: b(true),
  },
  moon: {
    display: e(["phase", "illumination", "both"], "both"),
    hemisphere: e(["north", "south", "source-derived"], "source-derived"),
    date: s("", 80),
    riseSet: b(false),
    labels: b(true),
  },
  pressure: {
    window: e(["1h", "24h", "7d", "30d"], "24h"),
    tendency: e(["measured-delta"], "measured-delta"),
    showHistory: b(true),
    units: e(supportedUnits, "inherit"),
  },
  action: {
    label: s("Action", 80),
    action: s("", 80),
    style: e(["filled", "quiet"], "filled"),
    icon: e(["none", "power", "check", "arrow", "reset"], "arrow"),
    confirmation: e(["action-policy"], "action-policy"),
    failureFeedback: e(["visible"], "visible"),
  },
  "active-button": {
    stateLabels: m(s(""), { true: "On", false: "Off" }, 16),
    style: e(["standard", "quiet", "overlay"], "standard"),
    pendingState: e(["visible"], "visible"),
    unavailableState: e(["disabled-with-reason"], "disabled-with-reason"),
  },
  rocker: {
    orientation: e(["horizontal", "vertical"], "horizontal"),
    positions: l(["Off", "On"], 6),
    labels: l(["Off", "On"], 6),
    tripState: e(["visible"], "visible"),
    commandFeedback: e(["required"], "required"),
  },
  "vertical-switch": {
    orientation: e(["vertical"], "vertical"),
    positions: l(["Off", "On"], 6),
    labelPosition: e(["above", "beside"], "above"),
    stateStyle: e(["text", "indicator"], "indicator"),
    momentary: e(["device-defined"], "device-defined"),
  },
  momentary: {
    activation: e(["device-policy"], "device-policy"),
    label: s("Hold", 80),
    icon: e(["none", "power", "check", "arrow", "reset"], "power"),
    pressDuration: e(["device-defined"], "device-defined"),
    backlight: e(["off", "state"], "state"),
    confirmation: e(["action-policy"], "action-policy"),
  },
  dimmer: {
    display: e(["slider", "two-button", "three-button"], "slider"),
    step: n(5, 0.1, 100),
    showPercent: b(true),
    minimum: n(0, 0, 100),
    offBehavior: e(["declared-policy"], "declared-policy"),
  },
  "lighting-color": {
    beamShape: e(["up", "down", "left", "right", "circle"], "circle"),
    display: e(["beam", "palette"], "beam"),
    colorMode: e(["rgb", "temperature", "device-declared"], "device-declared"),
    showIntensity: b(true),
    diagramPlacement: e(["inline", "centered"], "centered"),
  },
  climate: {
    fields: l(["temperature", "setpoint"], 8),
    setpointStep: n(1, 0.1, 10),
    bounds: a(n(0), [16, 30], 2),
    layout: e(["compact", "full"], "full"),
    showMode: b(true),
    showFan: b(true),
  },
  "ice-maker": {
    fields: l(["state", "temperature"], 8),
    commands: l([], 8),
    view: e(["compact", "full"], "full"),
    showFaults: b(true),
    deviceLabel: b(true),
  },
  watermaker: {
    fields: l(["production", "salinity"], 8),
    units: e(supportedUnits, "inherit"),
    view: e(["compact", "full"], "full"),
    showQuality: b(true),
    commandFeedback: e(["required"], "required"),
  },
  stereo: {
    zones: l([], 4),
    nowPlaying: b(true),
    volumeControls: e(["compact", "full"], "full"),
    layout: e(["compact", "full"], "full"),
    sourceSelector: b(true),
  },
  mode: {
    visibleModes: l([], 8),
    display: e(["segmented", "menu"], "segmented"),
    showReason: b(true),
    commandPolicy: e(["platform-defined"], "platform-defined"),
  },
  alerts: {
    severityFilters: l([], 4),
    categoryFilters: l([], 8),
    order: e(["severity", "time"], "severity"),
    maxRows: i(5, 1, 20),
    showAck: b(true),
    staleState: e(["visible"], "visible"),
  },
  watchkeeping: {
    display: e(["countdown", "state"], "countdown"),
    warningPhases: e(["service-defined"], "service-defined"),
    acknowledge: b(true),
    escalation: e(["service-defined"], "service-defined"),
    settings: e(["service-policy"], "service-policy"),
  },
  notification: {
    destination: s("", 80),
    template: s("", 80),
    trigger: e(["explicit-action"], "explicit-action"),
    deliveryFeedback: e(["visible"], "visible"),
    confirmation: e(["action-policy"], "action-policy"),
  },
  camera: {
    aspect: e(["4:3", "16:9", "source"], "16:9"),
    fit: e(["contain", "cover"], "contain"),
    showName: b(true),
    showTime: b(true),
    border: e(["normal", "none"], "normal"),
  },
  "camera-ptz": {
    controls: l(["pan", "tilt", "zoom"], 3),
    presets: l([], 12),
    speed: e(["slow", "normal", "fast"], "normal"),
    feedback: e(["required"], "required"),
    aspect: e(["source", "4:3", "16:9"], "16:9"),
  },
  "web-page": {
    source: s("", 80),
    aspect: e(["16:9", "4:3", "square"], "16:9"),
    navigation: e(["host-approved"], "host-approved"),
    unavailableFallback: e(["visible"], "visible"),
  },
  diagram: {
    asset: s("", 80),
    overlays: a(overlay, [], 20),
    labels: b(true),
    fit: e(["contain"], "contain"),
    actions: l([], 8),
  },
  note: {
    content: s("Operating note", 4000),
    style: e(["label", "heading", "note"], "note"),
    alignment: e(["start", "center"], "start"),
    wrap: b(true),
    procedureLink: s("", 80),
  },
  divider: {
    style: e(["space", "line", "heading"], "line"),
    spacing: e(["small", "medium", "large"], "medium"),
    label: s("", 80),
    visibility: e(["visible", "compact-only", "comfortable-only"], "visible"),
  },
  "power-budget": {
    houseBankId: s("", 80),
    concernSoc: n(30, 20, 80),
    fields: l(["soc", "voltage", "current"], 8),
    history: b(true),
    estimates: e(["labeled-with-confidence"], "labeled-with-confidence"),
  },
  "engine-vitals": {
    engineIds: l([], 4),
    probeKeys: l([], 8),
    fields: l(["rpm", "temperature", "pressure"], 8),
    arrangement: e(["paired", "stacked"], "paired"),
    health: e(["factual"], "factual"),
  },
  "tanks-budget": {
    prominentTanks: l([], 8),
    sort: e(["system", "name", "remaining"], "system"),
    display: e(["percent", "quantity", "both"], "both"),
    forecast: b(true),
  },
  "water-budget": {
    fields: l(["remaining", "consumption", "production"], 8),
    horizon: e(["day", "week", "trip"], "day"),
    units: e(supportedUnits, "inherit"),
    estimateLabels: e(["required"], "required"),
    detail: e(["compact", "full"], "full"),
  },
  "depth-safety": {
    referenceLabel: e(["required"], "required"),
    visualScale: e(["auto", "fixed"], "auto"),
    trend: e(["off", "short"], "short"),
    thresholdDisplay: e(["labeled"], "labeled"),
    operationalThreshold: e(["service-owned"], "service-owned"),
  },
  environment: {
    fields: l(["temperature", "pressure", "wind"], 8),
    layout: e(["compact", "full"], "full"),
    order: l([], 8),
    provenanceLabels: e(["required"], "required"),
    units: e(supportedUnits, "inherit"),
  },
  changes: {
    lookbackMinutes: i(30, 5, 60),
    fields: l([], 8),
    sort: e(["time", "magnitude"], "time"),
    maxRows: i(5, 1, 20),
    freshness: e(["visible"], "visible"),
  },
};
const groups = {
  angular: [
    "compass",
    "head-up",
    "wind-rose",
    "close-wind",
    "wind-angle",
    "rudder",
    "turn-rate",
    "heel",
    "attitude",
    "set-drift",
  ],
  readout: [
    "number",
    "trend-number",
    "state",
    "text-value",
    "multi-text",
    "status-strip",
    "counter",
    "timer",
    "analog-clock",
    "digital-clock",
  ],
  gauge: [
    "arc-gauge",
    "needle-gauge",
    "vacuum",
    "vertical-meter",
    "horizontal-meter",
    "borderless-bar",
    "tank",
  ],
  graph: [
    "sparkline",
    "history",
    "depth-graph",
    "state-history",
    "comparison-bars",
    "multi-graph",
  ],
  navigation: [
    "anchor",
    "gps",
    "signal",
    "board-health",
    "traffic",
    "route",
    "forecast",
    "tide",
    "moon",
    "pressure",
  ],
  command: [
    "action",
    "active-button",
    "rocker",
    "vertical-switch",
    "momentary",
    "dimmer",
    "lighting-color",
    "climate",
    "ice-maker",
    "watermaker",
    "stereo",
    "mode",
    "alerts",
    "watchkeeping",
    "notification",
  ],
  media: ["camera", "camera-ptz", "web-page", "diagram", "note", "divider"],
  composite: [
    "power-budget",
    "engine-vitals",
    "tanks-budget",
    "water-budget",
    "depth-safety",
    "environment",
    "changes",
  ],
};
const role = (
  kind = "number",
  required = true,
  dimension = "any",
  references = [],
) => ({ kind, required, dimension, references });
const roles = {
  compass: { heading: role("number", true, "angle", ["true", "magnetic"]) },
  "head-up": {
    heading: role("number", true, "angle"),
    course: role("number", false, "angle"),
    speed: role("number", false, "speed"),
  },
  "wind-rose": {
    apparentAngle: role("number", true, "angle", ["relative-apparent"]),
    apparentSpeed: role("number", true, "speed"),
    trueAngle: role("number", false, "angle", ["true-water", "true-ground"]),
    trueSpeed: role("number", false, "speed"),
    heading: role("number", false, "angle"),
  },
  "close-wind": {
    angle: role("number", true, "angle"),
    speed: role("number", false, "speed"),
  },
  "wind-angle": {
    angle: role("number", true, "angle"),
    speed: role("number", false, "speed"),
  },
  rudder: {
    actualAngle: role("number", true, "angle"),
    orderedAngle: role("number", false, "angle"),
  },
  "turn-rate": { turnRate: role("number", true, "angular-rate") },
  heel: {
    roll: role("number", true, "angle"),
    pitch: role("number", false, "angle"),
  },
  attitude: {
    roll: role("number", true, "angle"),
    pitch: role("number", true, "angle"),
    heading: role("number", false, "angle"),
  },
  "set-drift": {
    set: role("number", true, "angle"),
    drift: role("number", true, "speed"),
    heading: role("number", false, "angle"),
  },
  number: { value: role(), secondary: role("number", false) },
  "trend-number": { value: role() },
  state: { state: role("state") },
  "text-value": { value: role("text") },
  counter: { count: role() },
  timer: {
    duration: role("number", true, "time"),
    running: role("state", false),
  },
  "analog-clock": { time: role("time") },
  "digital-clock": { time: role("time") },
  tank: {
    level: role(),
    capacity: role("number", false),
    quantity: role("number", false),
  },
  moon: { time: role("time") },
};
for (const id of groups.gauge) roles[id] ??= { value: role() };
for (const id of groups.graph)
  roles[id] ??= {
    value: role(
      id === "state-history"
        ? "state-series"
        : id === "comparison-bars"
          ? "categories"
          : "series",
    ),
  };
for (const id of [...groups.navigation, ...groups.command, ...groups.composite])
  roles[id] ??= { service: role("service") };
for (const id of ["multi-text", "status-strip"])
  roles[id] = { rows: role("collection") };
for (const id of ["camera", "camera-ptz", "web-page", "diagram"])
  roles[id] = { source: role("media") };
roles.note = {};
roles.divider = {};
const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
export const definitions = catalog.map((c) => {
  const family = Object.keys(groups).find((k) => groups[k].includes(c.id));
  if (!family || !options[c.id] || !roles[c.id])
    throw new Error(`Incomplete definition ${c.id}`);
  const properties = { ...commonFields, ...options[c.id] };
  const defaults = Object.fromEntries(
    Object.entries(properties).map(([k, v]) => [k, structuredClone(v.default)]),
  );
  defaults.title = c.name;
  const presets = c.presets.split(" · ").map((title, index) => ({
    id: slug(title),
    title,
    options: {
      title,
      ...(c.id === "wind-rose" && index > 0 ? { centerRole: "trueSpeed" } : {}),
    },
  }));
  return Object.freeze({
    ...c,
    catalogStatus: c.status,
    status: "implemented",
    definitionVersion: 1,
    family,
    roles: roles[c.id],
    defaults,
    optionsSchema: {
      $schema: "https://json-schema.org/draft/2020-12/schema",
      type: "object",
      additionalProperties: false,
      properties,
    },
    presets,
    renderer: c.id,
  });
});
const byId = new Map(definitions.map((d) => [d.id, d]));
export function getDefinition(id) {
  const d = byId.get(id);
  if (!d)
    throw new ValidationError([
      { path: "definitionId", message: `Unknown control ${id}` },
    ]);
  return d;
}
export function discoverControls(query = "") {
  const q = query.toLowerCase();
  return definitions
    .filter(
      (d) =>
        !q ||
        `${d.id} ${d.name} ${d.presets.map((p) => p.title).join(" ")} ${d.bindings}`
          .toLowerCase()
          .includes(q),
    )
    .map((d) => ({
      id: d.id,
      name: d.name,
      family: d.family,
      roles: d.roles,
      presets: d.presets,
      editableFields: Object.keys(d.optionsSchema.properties),
      clients: ["web", "ios"],
    }));
}
export function validateOptions(id, patch, complete = false) {
  const aliases = {
    standard: "detailed",
    minimal: "simple",
    classic: "detailed",
  };
  if (patch && Object.hasOwn(aliases, patch.variant))
    patch = { ...patch, variant: aliases[patch.variant] };
  const d = getDefinition(id);
  assertValid(d.optionsSchema, patch);
  const v = { ...d.defaults, ...patch };
  const issues = [];
  for (const key of ["bounds", "range", "scale", "expandedArc"])
    if (
      Array.isArray(v[key]) &&
      (v[key].length !== 2 || v[key][0] >= v[key][1])
    )
      issues.push({
        path: `options.${key}`,
        message: "Requires two ordered bounds",
      });
  if (v.scaleMin >= v.scaleMax)
    issues.push({ path: "options.scaleMax", message: "Must exceed scaleMin" });
  if ("min" in v && "max" in v && v.min >= v.max)
    issues.push({ path: "options.max", message: "Must exceed min" });
  for (const z of v.zones ?? [])
    if (z.min >= z.max)
      issues.push({
        path: "options.zones",
        message: "Zone minimum must be below maximum",
      });
  for (const key of ["timezone", "zone"])
    if (v[key])
      try {
        new Intl.DateTimeFormat("en", { timeZone: v[key] });
      } catch {
        issues.push({ path: `options.${key}`, message: "Unknown timezone" });
      }
  if (v.frame === "absolute" && v.orientation === "bow-up")
    issues.push({
      path: "options.orientation",
      message: "Absolute wind uses north-up orientation",
    });
  if (issues.length) throw new ValidationError(issues);
  return complete ? v : structuredClone(patch);
}
export function createInstance(definitionId, input = {}) {
  assertSafe(input);
  const d = getDefinition(definitionId);
  const preset = d.presets.find((p) => p.id === input.presetId);
  if (input.presetId && !preset)
    throw new ValidationError([
      { path: "presetId", message: "Unknown preset" },
    ]);
  const legacySets = {
    "tombolo.standard": "tombolo.detailed",
    "tombolo.classic": "tombolo.detailed",
    "tombolo.minimal": "tombolo.simple",
  };
  const setId = Object.hasOwn(legacySets, input.setId)
    ? legacySets[input.setId]
    : (input.setId ??
      (input.options?.variant === "simple" ||
      input.options?.variant === "minimal"
        ? "tombolo.simple"
        : "tombolo.detailed"));
  const instance = {
    v: 1,
    id: input.id ?? crypto.randomUUID(),
    boatId: input.boatId ?? "",
    boardId: input.boardId ?? "",
    definitionId,
    definitionVersion: 1,
    setId,
    setVersion: input.setVersion ?? 1,
    presetId: input.presetId ?? null,
    bindings: structuredClone(input.bindings ?? {}),
    options: validateOptions(
      definitionId,
      {
        ...d.defaults,
        ...(setId === "tombolo.simple"
          ? { variant: "simple", animationDuration: 1000 }
          : {}),
        ...preset?.options,
        ...input.options,
      },
      true,
    ),
  };
  validateBindings(instance);
  return instance;
}
export function validateBindings(instance) {
  const d = getDefinition(instance.definitionId);
  const issues = [];
  for (const [name, binding] of Object.entries(instance.bindings ?? {})) {
    if (!d.roles[name]) {
      issues.push({ path: `bindings.${name}`, message: "Unsupported role" });
      continue;
    }
    if (
      !binding ||
      typeof binding !== "object" ||
      typeof binding.sourceId !== "string" ||
      !binding.sourceId
    )
      issues.push({
        path: `bindings.${name}`,
        message: "Stable sourceId required",
      });
    else {
      const r = d.roles[name];
      if (
        binding.dimension &&
        r.dimension !== "any" &&
        binding.dimension !== r.dimension
      )
        issues.push({
          path: `bindings.${name}.dimension`,
          message: "Incompatible dimension",
        });
      if (
        binding.reference &&
        r.references.length &&
        !r.references.includes(binding.reference)
      )
        issues.push({
          path: `bindings.${name}.reference`,
          message: "Incompatible reference",
        });
      if (
        Object.keys(binding).some(
          (k) =>
            ![
              "sourceId",
              "selection",
              "dimension",
              "reference",
              "entityId",
              "role",
            ].includes(k),
        )
      )
        issues.push({
          path: `bindings.${name}`,
          message: "Unsupported binding metadata",
        });
      if (
        binding.selection &&
        !["canonical", "physical"].includes(binding.selection)
      )
        issues.push({
          path: `bindings.${name}.selection`,
          message: "Unknown source selection",
        });
    }
  }
  if (instance.options.frame === "absolute" && !instance.bindings.heading)
    issues.push({
      path: "bindings.heading",
      message: "Absolute wind requires a compatible heading binding",
    });
  if (issues.length) throw new ValidationError(issues);
  return instance;
}
export function getDataRequest(instance) {
  const d = getDefinition(instance.definitionId);
  return {
    instanceId: instance.id,
    definitionId: d.id,
    roles: d.roles,
    bindings: structuredClone(instance.bindings),
    history: {
      window: instance.options.window ?? instance.options.historyWindow,
      rolling: instance.options.rolling,
      aggregation: instance.options.aggregation,
    },
    options: structuredClone(instance.options),
  };
}

deepFreeze(definitions);
deepFreeze(commonFields);
